import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import TipsWrap from 'common/components/tips-wrap';
import item from '~pages/onboarding/property-type/item';
import { COMPANY_TYPE } from '~constants/auth';
import { Title } from './personal-info';
import FieldCard from './field-card';

const uesStyles = makeStyles(
  theme => ({
    root: {
      width: '100%',
      minHeight: 400,
      paddingLeft: 32,
      overflowY: 'auto',
    },
    content: {
      position: 'relative',
    },
    tipsWrap: {
      ...theme.typography.body4,
      position: 'absolute',
      top: 0,
      left: '60%',
      width: 266,
    },
    openEmailText: {
      color: `${theme.colors.mainGreen6} !important`,
    },
    loading: {
      ...theme.flex.center,
      height: '100%',
    },
  }),
  { name: 'CompanyInfo' },
);

const CompanyInfoTipsWrap = () => {
  const classes = uesStyles();
  const { t } = useTranslation();

  return (
    <TipsWrap className={classes.tipsWrap}>
      <div style={{ marginTop: 10 }}>
        <span>{t('having_any_problems_please_link')}</span>
        <a target="_blank" href="mailto:contact@student.com" rel="noreferrer">
          <span className={classes.openEmailText}>{t('contact_our_staff')}</span>
        </a>
      </div>
    </TipsWrap>
  );
};

const CompanyInfo = ({ landlordCompanyData }) => {
  const classes = uesStyles();
  const { t } = useTranslation();

  const businessType = landlordCompanyData?.businessType || 'COMPANY';
  const companyInfo = useMemo(
    () => [
      { label: t('company_name'), value: landlordCompanyData?.name ?? '-' },
      {
        label: t('landlord.onboarding.label.company_type'),
        value: landlordCompanyData?.companyType
          ? COMPANY_TYPE[landlordCompanyData?.companyType]
          : '-',
      },
      { label: t('business_opportunity'), value: landlordCompanyData?.businessScale ?? '-' },
      { label: t('registered_business_number'), value: landlordCompanyData?.businessNumber ?? '-' },
      {
        label: t('registered_business_address'),
        value: landlordCompanyData?.billingAddress ?? '-',
      },
      { label: t('zip_code'), value: landlordCompanyData?.billingPostalCode ?? '-' },
      {
        label: t('city'),
        value:
          landlordCompanyData?.billingCity?.name || landlordCompanyData?.billingCountry?.name
            ? ''.concat(
                `${landlordCompanyData?.billingCity?.name}, `,
                landlordCompanyData?.billingCountry?.name,
              )
            : '-',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      landlordCompanyData?.billingAddress,
      landlordCompanyData?.billingCity?.name,
      landlordCompanyData?.billingCountry?.name,
      landlordCompanyData?.billingPostalCode,
      landlordCompanyData?.businessNumber,
      landlordCompanyData?.businessScale,
      landlordCompanyData?.name,
      t,
    ],
  );

  const IndividualInfo = useMemo(
    () => [
      {
        label: t('landlord.my_account.company.individual.legal_name'),
        value:
          landlordCompanyData?.firstName && landlordCompanyData?.lastName
            ? `${landlordCompanyData?.firstName} ${landlordCompanyData?.lastName}`
            : '-',
      },
      {
        label: t('landlord.my_account.company.individual.bussiness_type'),
        value: landlordCompanyData?.businessType === 'INDIVIDUAL' ? 'Individual' : 'Company',
      },
      { label: t('business_opportunity'), value: landlordCompanyData?.businessScale ?? '-' },
      {
        label: t('landlord.my_account.company.individual.government_issued_id'),
        value: landlordCompanyData?.governmentIssuedId ?? '-',
      },
      {
        label: t('landlord.my_account.company.individual.address'),
        value: landlordCompanyData?.billingAddress ?? '-',
      },
      { label: t('zip_code'), value: landlordCompanyData?.billingPostalCode ?? '-' },
      {
        label: t('city'),
        value:
          landlordCompanyData?.billingCity?.name || landlordCompanyData?.billingCountry?.name
            ? ''.concat(
                `${landlordCompanyData?.billingCity?.name}, `,
                landlordCompanyData?.billingCountry?.name,
              )
            : '-',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      landlordCompanyData?.firstName,
      landlordCompanyData?.lastName,
      landlordCompanyData?.businessType,
      landlordCompanyData?.businessScale,
      landlordCompanyData?.governmentIssuedId,
      landlordCompanyData?.billingAddress,
      landlordCompanyData?.businessNumber,
      landlordCompanyData?.billingPostalCode,
      landlordCompanyData?.billingCity?.name,
      landlordCompanyData?.billingCountry?.name,
      t,
    ],
  );

  return (
    <div className={classes.root}>
      <If condition={businessType === 'COMPANY'}>
        <Title
          title={t('company_info')}
          desc={t('this_page_is_for_business_registered_related_information')}
        />
      </If>
      <If condition={businessType === 'INDIVIDUAL'}>
        <Title
          title={t('landlord.my_account.company.individual.title')}
          desc={t('landlord.my_account.company.individual.desc')}
        />
      </If>

      <div className={classes.content}>
        <If condition={businessType === 'COMPANY'}>
          <For of={companyInfo} each="item">
            <div key={item.label}>
              <FieldCard label={item.label} value={item.value} readOnly />
            </div>
          </For>
        </If>
        <If condition={businessType === 'INDIVIDUAL'}>
          <For of={IndividualInfo} each="item">
            <div key={item.label}>
              <FieldCard label={item.label} value={item.value} readOnly />
            </div>
          </For>
        </If>
        <CompanyInfoTipsWrap />
      </div>
    </div>
  );
};

CompanyInfo.propTypes = { landlordCompanyData: PropTypes.object.isRequired };

export default CompanyInfo;
